import { useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { upsertProfileCache, PROFILE_KIND_LABEL, needsFullName } from "@/lib/profiles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Field, SimpleSelect } from "@/components/wizard/Field";
import { LANGUAGES } from "@/constants/catalog";
import { t } from "@/i18n";

const EMPTY = { name: "", kind: "artist", apple_id: "", apple_new: true, spotify_uri: "", spotify_new: true, soundcloud_url: "", amazon_asin: "", isni: "", localizations: [], notes: "" };
const KINDS = Object.entries(PROFILE_KIND_LABEL);

const DspField = ({ label, placeholder, value, onValue, isNew, onNew, newLabel, testId }) => (
  <Field label={label}>
    <Input value={value} onChange={(e) => onValue(e.target.value)} placeholder={placeholder} disabled={isNew} className="font-mono text-sm" data-testid={`${testId}-input`} />
    <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer pt-1">
      <Checkbox checked={isNew} onCheckedChange={(v) => onNew(!!v)} data-testid={`${testId}-new-checkbox`} /> {newLabel}
    </label>
  </Field>
);

export const ProfileDialog = ({ open, onClose, initial, onSaved }) => {
  const [f, setF] = useState({ ...EMPTY, ...(initial || {}), apple_new: initial?.id ? !!initial.apple_new : !initial?.apple_id, spotify_new: initial?.id ? !!initial.spotify_new : !initial?.spotify_uri });
  const [busy, setBusy] = useState(false);
  const set = (patch) => setF((x) => ({ ...x, ...patch }));
  const editing = !!initial?.id;
  const fullName = needsFullName(f.kind);
  const nameInvalid = fullName && f.name.trim().split(/\s+/).length < 2;

  const submit = async (e) => {
    e.preventDefault();
    if (nameInvalid) return toast.error(t("Downtown exige nombre y apellido para compositores y contributors"));
    setBusy(true);
    try {
      const body = { ...f, apple_id: f.apple_new ? "" : f.apple_id, spotify_uri: f.spotify_new ? "" : f.spotify_uri };
      const { data } = editing ? await api.put(`/profiles/${initial.id}`, body) : await api.post("/profiles", body);
      upsertProfileCache(data);
      toast.success(editing ? t("Perfil actualizado") : t("Perfil «{name}» creado", { name: data.name }));
      onSaved?.(data);
      onClose();
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const locs = f.localizations || [];
  const setLoc = (i, patch) => set({ localizations: locs.map((l, j) => (j === i ? { ...l, ...patch } : l)) });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="profile-dialog">
        <DialogHeader>
          <DialogTitle>{t(editing ? "Editar perfil" : "Crear perfil de artista / compositor")}</DialogTitle>
          <DialogDescription>{t("Estos son los datos que pide la distribuidora al dar de alta un artista o compositor. Si no tienes los IDs, marca \"nuevo\" y se creará el perfil en la tienda.")}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_220px] gap-4">
            <Field label="Nombre" hint={fullName ? "Compositores / letristas: NOMBRE Y APELLIDO reales (la distribuidora lo exige)" : "Nombre artístico tal como debe aparecer en las tiendas"}>
              <Input required value={f.name} onChange={(e) => set({ name: e.target.value })} placeholder={fullName ? "Juan Pérez" : "DRAC"} data-testid="profile-name-input" className={nameInvalid && f.name ? "border-destructive" : ""} />
            </Field>
            <Field label="Tipo"><SimpleSelect value={f.kind} onChange={(v) => set({ kind: v })} options={KINDS} testId="profile-kind-select" /></Field>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <DspField label="Apple ID" placeholder="1234567890" value={f.apple_id || ""} onValue={(v) => set({ apple_id: v })} isNew={f.apple_new} onNew={(v) => set({ apple_new: v })} newLabel={t("Nuevo artista en Apple Music")} testId="profile-apple" />
            <DspField label="Spotify URI" placeholder="spotify:artist:…" value={f.spotify_uri || ""} onValue={(v) => set({ spotify_uri: v })} isNew={f.spotify_new} onNew={(v) => set({ spotify_new: v })} newLabel={t("Nuevo artista en Spotify")} testId="profile-spotify" />
            <Field label="SoundCloud URL"><Input value={f.soundcloud_url || ""} onChange={(e) => set({ soundcloud_url: e.target.value })} placeholder="https://soundcloud.com/…" data-testid="profile-soundcloud-input" /></Field>
            <Field label="Amazon ASIN"><Input value={f.amazon_asin || ""} onChange={(e) => set({ amazon_asin: e.target.value })} placeholder="B0…" className="font-mono text-sm" data-testid="profile-amazon-input" /></Field>
            <Field label="ISNI" hint="Opcional · identificador internacional"><Input value={f.isni || ""} onChange={(e) => set({ isni: e.target.value })} placeholder="0000 0001 2345 6789" className="font-mono text-sm" data-testid="profile-isni-input" /></Field>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-xs text-muted-foreground uppercase tracking-wider">{t("Localizaciones del nombre (opcional)")}</p>
              <button type="button" className="text-xs text-primary hover:underline flex items-center gap-1" onClick={() => set({ localizations: [...locs, { language: "en", name: "" }] })} data-testid="profile-add-localization"><Plus className="h-3 w-3" /> {t("Agregar")}</button>
            </div>
            {locs.map((l, i) => (
              <div key={i} className="grid grid-cols-[150px_1fr_32px] gap-2 items-center">
                <SimpleSelect value={l.language} onChange={(v) => setLoc(i, { language: v })} options={LANGUAGES} testId={`profile-loc-lang-${i}`} />
                <Input value={l.name} onChange={(e) => setLoc(i, { name: e.target.value })} placeholder={t("Nombre en ese idioma")} data-testid={`profile-loc-name-${i}`} className="h-9" />
                <button type="button" onClick={() => set({ localizations: locs.filter((_, j) => j !== i) })} className="text-muted-foreground hover:text-destructive" data-testid={`profile-loc-remove-${i}`}><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <Field label="Notas internas"><Textarea rows={2} value={f.notes || ""} onChange={(e) => set({ notes: e.target.value })} data-testid="profile-notes-input" /></Field>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose} data-testid="profile-cancel-button">{t("Cancelar")}</Button>
            <Button type="submit" disabled={busy} data-testid="profile-submit-button">{t(busy ? "Guardando…" : editing ? "Guardar cambios" : "Crear perfil")}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
